import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Subscription, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { Mention } from '../../../../Common/Mentions/models/mention';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BriefingsService } from '../../services/briefings.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { BlockModel, LayoutBlockTypesEnum } from '../../../../Common/PageBuilder/models/block.model';
import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { BadgeSectionEnum,
    MentionBadge,
    mentionBadgesSaved,
    MentionBadgeTypeEnum
} from '../../../../Common/Mentions/components/MentionCard/mention-card.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { AddMentionsWidgetComponent } from '../../../Shared/components/AddMentionsWidget/add-mentions-widget.component';
import { LayoutBlock } from '../../../../Common/PageBuilder/models/layout-block.model';
import { BlockDisplayTypeEnum } from '@Common/PageBuilder/enums/block-display-type.enum';
import { BriefingModel } from '@Monitor/Briefings/models/briefing.model';
import { BriefingDeliveryMethodEnum } from '@Monitor/Briefings/enums/briefing-delivery-method.enum';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { CuratorBlock } from '../../../../Common/PageBuilder/models/curator-block.model';
import { MentionSearchCriteriaModel } from '../../../Shared/models/mention-search-criteria.model';


@Component({
    selector: 'agpr-manually-add-coverage-dialog',
    templateUrl: './manually-add-coverage-dialog.component.html',
    styleUrls: ['./manually-add-coverage-dialog.component.scss'],
    standalone: false
})
export class ManuallyAddCoverageDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() set block(block: BlockModel) {
        this.selectedBlock = block;
        this.updateDestinations();
    }
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('addMentionsWidget') addMentionsWidget: AddMentionsWidgetComponent;
    @ViewChild(ScrollPanelComponent) scrollPanel: ScrollPanelComponent;

    mentionBadges: MentionBadge[] = [
        ...mentionBadgesSaved,
        {
            type: MentionBadgeTypeEnum.MENTION_IN_BRIEFING,
            icon: 'fa-kit-duotone fa-agpr-check-content',
            tooltip: 'Briefings.mentions.badge.inBriefing',
            style: {
                color: chartColors.agprBlue
            },
            section: BadgeSectionEnum.second,
            condition: (mention: Mention) => {
                return this.isInBriefing(mention);
            }
        }
    ];
    hideSlideOverlay: boolean = false;

    destinationOptions: DropdownOptionModel<BlockModel>[] = [];
    destinationOptions$: Observable<DropdownOptionModel<BlockModel>[]>;

    selectedDestination: DropdownOptionModel;

    private availableMentions: Mention[] = [];
    private selectedMentions: Mention[] = [];

    private currentBriefing: BriefingModel;
    private briefingMentions: Mention[] = [];
    private curatedBlocks: BlockModel[];

    private selectedBlock: BlockModel;

    private subscriptions: Subscription[] = [];

    readonly id: string = 'briefingManuallyAddCoverageDialog';
    readonly buttonTypes = ButtonTypesEnum;
    readonly doneWarningHeading: string = 'Briefings.importTool.warningDone';
    readonly customOptions: AutocompleteCustomOption[] = [
        {
            label: 'Briefings.importTool.autoSort',
            action: (searchString: string) => {},
            appearCondition: AppearCondition.always,
            clearOnSelect: true
        },
        {
            label: 'Briefings.importTool.createNewSection',
            action: (searchString: string) => {
                this.selectedBlock = this.briefingsService.addCuratedBlock(searchString);
                this.briefingsService.updateBlocks();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Briefings.addCoverage.newSectionAddedHeading',
                    detail: this.localizationService.transform('Briefings.addCoverage.newSectionAdded', { sectionName: searchString })
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: false
        }
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private briefingsService: BriefingsService,
        private messageService: MessageService
    ) {
        super(coreDialogService, 'briefingImportMentionDialog');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingsService.getCurrentBriefingMentions().subscribe(
                mentions => {
                    if (!mentions) {
                        this.briefingMentions = [];
                        return;
                    }

                    this.briefingMentions = mentions;
                    // Causes the flags to get updated
                    this.mentionBadges = this.mentionBadges.slice();
                }
            ),
            this.briefingsService.getFocusedResource().pipe(
                tap((currentBriefing) => this.currentBriefing = currentBriefing),
                switchMap(() => this.briefingsService.getBlocks())
            ).subscribe((blocks) => {
                this.curatedBlocks = [];
                blocks.forEach(block => {
                    if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                        (block as LayoutBlock).blocks.forEach(column => {
                            column.forEach(x => {
                                if (x.type && x.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                                    if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                                        x.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
                                    }
                                    this.curatedBlocks.push(x);
                                }
                            });
                        });
                    } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                        (block as CuratorBlock).blocks.forEach(x => {
                            if (x.type?.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                                if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                                    x.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
                                }
                                this.curatedBlocks.push(x);
                            }
                        });
                    } else {
                        if (block.type && block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                            if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                                block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
                            }
                            this.curatedBlocks.push(block);
                        }
                    }
                });
                this.updateDestinations();
            })
        );
    }


    /**
     * Hides the import dialog
     */
    doHide() {
        this.hideSlideOverlay = true;
        this.reset();
        this.closeDialog();
    }

    /**
     * Clears the results of the dialog, and resets the form to original values
     */
    reset() {
        if (this.addMentionsWidget.sourcePicker) {
            this.addMentionsWidget.sourcePicker.resetSourcePicker();
        }

        this.addMentionsWidget.selectNone();
    }

    /**
     * Adds the selected mentions to the briefing
     */
    addMentions() {
        const mentions = this.getOrderedSelectedMentions();

        if (this.selectedBlock) {
            this.briefingsService.addMentionsToBlock(this.selectedBlock, mentions);
        } else {
            /** @hack for updating styles on mentionOfInterestBlock */
            const mentionsOfInterestTitle = this.briefingsService.getMentionBlockName();
            const mentionsOfInterestBeforeAddingMentions = this.curatedBlocks.find((block) => {
                const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                return displayElement.title === mentionsOfInterestTitle;
            });
            // end hack

            this.briefingsService.addMentionsToCuratedBlocks(mentions);

            /** @hack for updating styles on mentionOfInterestBlock */
            const mentionsOfInterestAfterAddingMentions = this.curatedBlocks.find((block) => {
                const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                return displayElement.title === mentionsOfInterestTitle;
            });

            if (!mentionsOfInterestBeforeAddingMentions && !!mentionsOfInterestAfterAddingMentions) {
                // new mentions of interest block was added
                this.briefingsService.applyGeneralStylesToBlock(mentionsOfInterestAfterAddingMentions);
            }
            // end hack
        }
        this.addMentionsWidget.selectNone();

        this.subscriptions.push(
            this.briefingsService.checkMaxMentionsExceeded()
                .subscribe((maxMentionsExceeded) => {
                    if (maxMentionsExceeded) {
                        this.briefingsService.showMentionLimitReachedDialog();
                    }
                })
        );
    }


    /**
     * Updates the list of available destinations and the current selected destination
     */
    private updateDestinations(): void {
        if (this.curatedBlocks) {
            this.destinationOptions = this.curatedBlocks.map((curatedBlock) => {
                const displayElement = curatedBlock.displayElement as CuratedMentionListDisplayElementModel;
                return new DropdownOptionModel<BlockModel>(displayElement.title, curatedBlock);
            });
        } else {
            this.destinationOptions = [];
        }

        this.destinationOptions$ = of(this.destinationOptions);

        this.selectedDestination = this.destinationOptions.find((option) => {
            return option.value === this.selectedBlock;
        });
    }

    selectDestination(option: DropdownOptionModel): void {
        this.selectedDestination = option;
        if (option) {
            this.selectedBlock = option.value;
        } else {
            this.selectedBlock = null;
        }
    }

    isInBriefing(mention: Mention): boolean {
        return !!this.briefingMentions.find(briefingMention => {
            if (mention.id) {
                return briefingMention.id === mention.id;
            }

            if (mention.mentionId) {
                return briefingMention.mentionId === mention.mentionId;
            }

            return false;
        });
    }

    setAvailableMentions(mentions: Mention[]) {
        this.availableMentions = mentions;
    }

    setSelectedMentions(mentions: Mention[]) {
        this.selectedMentions = mentions;
    }

    /**
     * Gets all of the currently selected mentions
     */
    private getOrderedSelectedMentions(): Mention[] {
        return this.availableMentions.filter(mention => this.selectedMentions.includes(mention));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
